// src/modules/ida/components/HeartbeatEditor.tsx
//
// What? Outlook-style editor modal for an existing recurring heartbeat.
// Why? Phase 2b spec sec 7. Users adjust prompt + recurrence pattern;
//      create is agent-only. Save cancels + recreates the hub task.
// How? MUI Joy Modal + structured recurrence controls that compile down
//      to a standard 5-field cron expression before submit. Custom-cron
//      editing stays behind ?debug=1 so the normal UI only exposes the
//      meaningful presets.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  Radio,
  RadioGroup,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";

import { useAuthFetch } from "../../../utils/useAuthFetch";
import {
  heartbeatApi,
  HeartbeatDetail,
  HeartbeatEditPayload,
  HeartbeatSummary,
} from "../services/heartbeats";
import {
  buildHeartbeatCron,
  DEFAULT_WEEKDAYS,
  getBrowserTimezone,
  heartbeatDebugEnabled,
  parseHeartbeatCron,
  RepeatMode,
  toLocalDateInput,
  toLocalTimeInput,
  WEEKDAY_LABELS,
} from "../utils/heartbeatRecurrence";

interface HeartbeatEditorProps {
  open: boolean;
  workspaceId: string;
  task: HeartbeatSummary;
  onSave: (payload: HeartbeatEditPayload) => Promise<void>;
  onClose: () => void;
}

const HEARTBEAT_LOAD_ABORT_MESSAGE =
  "The heartbeat details request was interrupted. Retry loading details before saving.";
const HEARTBEAT_SAVE_ABORT_MESSAGE =
  "The heartbeat save request was interrupted. Please try again.";

function isAbortError(err: unknown): boolean {
  return (
    (typeof DOMException !== "undefined" &&
      err instanceof DOMException &&
      err.name === "AbortError") ||
    (err as { name?: string })?.name === "AbortError"
  );
}

export const HeartbeatEditor: React.FC<HeartbeatEditorProps> = ({
  open,
  workspaceId,
  task,
  onSave,
  onClose,
}) => {
  const authFetch = useAuthFetch("json");
  const debugEnabled = useMemo(() => heartbeatDebugEnabled(), []);
  const browserTimezone = useMemo(() => getBrowserTimezone() || "UTC", []);
  const authFetchRef = useRef(authFetch);
  const loadRequestIdRef = useRef(0);
  const [detail, setDetail] = useState<HeartbeatDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  const [prompt, setPrompt] = useState<string>(task.prompt ?? "");
  const [repeatMode, setRepeatMode] = useState<RepeatMode>("daily");
  const [hhmm, setHhmm] = useState<string>("09:00");
  const [weekdays, setWeekdays] = useState<boolean[]>([...DEFAULT_WEEKDAYS]);
  const [monthDay, setMonthDay] = useState<number>(1);
  const [hourlyStartDate, setHourlyStartDate] = useState<string>(
    toLocalDateInput(task.next_fire_at),
  );
  const [hourlyStartTime, setHourlyStartTime] = useState<string>(
    toLocalTimeInput(task.next_fire_at),
  );
  const [customCron, setCustomCron] = useState<string>("0 9 * * *");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const resetFormFromTaskSummary = useCallback(() => {
    setDetail(null);
    setPrompt(task.prompt ?? "");
    setRepeatMode("daily");
    setHhmm("09:00");
    setWeekdays([...DEFAULT_WEEKDAYS]);
    setMonthDay(1);
    setHourlyStartDate(toLocalDateInput(task.next_fire_at));
    setHourlyStartTime(toLocalTimeInput(task.next_fire_at));
    setCustomCron("0 9 * * *");
  }, [task.prompt, task.next_fire_at]);

  // Pre-fill from hub detail on open.
  useEffect(() => {
    if (!open) {
      loadRequestIdRef.current += 1;
      setLoading(false);
      return;
    }
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    let active = true;
    resetFormFromTaskSummary();
    setLoading(true);
    setError(null);
    heartbeatApi
      .get(authFetchRef.current, workspaceId, task.task_id)
      .then((heartbeatDetail) => {
        if (!active || requestId !== loadRequestIdRef.current) return;
        setDetail(heartbeatDetail);
        setPrompt(
          typeof heartbeatDetail.prompt === "string"
            ? heartbeatDetail.prompt
            : (task.prompt ?? ""),
        );
        if (
          typeof heartbeatDetail.cron_expression === "string" &&
          heartbeatDetail.cron_expression
        ) {
          const parsed = parseHeartbeatCron(
            heartbeatDetail.cron_expression,
            heartbeatDetail.next_fire_at ?? task.next_fire_at,
          );
          setCustomCron(parsed.customCron);
          setRepeatMode(parsed.repeatMode);
          setHhmm(parsed.hhmm);
          setWeekdays(parsed.weekdays);
          setMonthDay(parsed.monthDay);
          setHourlyStartDate(parsed.hourlyStartDate);
          setHourlyStartTime(parsed.hourlyStartTime);
        } else {
          setHourlyStartDate(
            toLocalDateInput(heartbeatDetail.next_fire_at ?? task.next_fire_at),
          );
          setHourlyStartTime(
            toLocalTimeInput(heartbeatDetail.next_fire_at ?? task.next_fire_at),
          );
        }
      })
      .catch((err) => {
        if (!active || requestId !== loadRequestIdRef.current) return;
        setError(
          isAbortError(err)
            ? HEARTBEAT_LOAD_ABORT_MESSAGE
            : (err as Error).message || "Failed to load heartbeat",
        );
      })
      .finally(() => {
        if (active && requestId === loadRequestIdRef.current) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [
    open,
    workspaceId,
    task.task_id,
    task.prompt,
    task.next_fire_at,
    loadAttempt,
    resetFormFromTaskSummary,
  ]);

  const effectiveCron = useMemo(() => {
    return buildHeartbeatCron({
      repeatMode,
      hhmm,
      weekdays,
      monthDay,
      customCron,
      hourlyStartTime,
    });
  }, [repeatMode, hhmm, weekdays, monthDay, customCron, hourlyStartTime]);

  const canSave = Boolean(
    !loading && !error && prompt.trim() && effectiveCron && browserTimezone,
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSave({
        cron_expression: effectiveCron,
        prompt: prompt.trim(),
        timezone: browserTimezone,
        agent_model: (detail?.agent_model as string | null) ?? null,
        conversation_id: task.conversation_id,
      });
    } catch (err) {
      // Surface 429 quota / 403 forbidden / 409 stale verbatim to the user.
      const status = (err as { status?: number }).status;
      let msg = (err as Error).message || "Save failed";
      if (status === 409) {
        msg =
          "This heartbeat was changed elsewhere. Please re-open and try again.";
      } else if (isAbortError(err)) {
        msg = HEARTBEAT_SAVE_ABORT_MESSAGE;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetryLoad = () => {
    setLoadAttempt((current) => current + 1);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ minWidth: 440, maxWidth: 520 }}>
        <ModalClose />
        <Typography level="title-md">Edit heartbeat</Typography>
        <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 1 }}>
          Updates this heartbeat in place. The task id stays the same.
        </Typography>

        {loading && (
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            Loading heartbeat details…
          </Typography>
        )}

        {error && (
          <Alert color="danger" variant="soft" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {!loading && !detail && error && (
          <Button
            size="sm"
            variant="soft"
            color="neutral"
            onClick={handleRetryLoad}
          >
            Retry loading details
          </Button>
        )}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl>
            <FormLabel>Prompt</FormLabel>
            <Textarea
              minRows={3}
              maxRows={6}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="What should the agent do when this heartbeat fires?"
              data-testid="heartbeat-editor-prompt"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Repeats</FormLabel>
            <RadioGroup
              orientation="horizontal"
              value={repeatMode}
              onChange={(e) => setRepeatMode(e.target.value as RepeatMode)}
            >
              <Radio value="hourly" label="Hourly" />
              <Radio value="daily" label="Daily" />
              <Radio value="weekly" label="Weekly" />
              <Radio value="monthly" label="Monthly" />
              {debugEnabled && <Radio value="custom" label="Custom cron" />}
            </RadioGroup>
          </FormControl>

          {repeatMode === "hourly" && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Start date</FormLabel>
                <Input
                  type="date"
                  value={hourlyStartDate}
                  onChange={(e) => setHourlyStartDate(e.target.value)}
                  slotProps={{
                    input: { "data-testid": "heartbeat-editor-hourly-date" },
                  }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Start time</FormLabel>
                <Input
                  type="time"
                  value={hourlyStartTime}
                  onChange={(e) => setHourlyStartTime(e.target.value)}
                  slotProps={{
                    input: {
                      step: 300,
                      "data-testid": "heartbeat-editor-hourly-time",
                    },
                  }}
                />
              </FormControl>
            </Stack>
          )}

          {repeatMode === "daily" && (
            <FormControl>
              <FormLabel>Time (24h)</FormLabel>
              <Input
                type="time"
                value={hhmm}
                onChange={(e) => setHhmm(e.target.value)}
                slotProps={{ input: { step: 300 } }}
              />
            </FormControl>
          )}

          {repeatMode === "weekly" && (
            <>
              <FormControl>
                <FormLabel>On days</FormLabel>
                <Stack direction="row" spacing={1}>
                  {WEEKDAY_LABELS.map((label, idx) => (
                    <Checkbox
                      key={`${label}-${idx}`}
                      label={label}
                      checked={weekdays[idx]}
                      onChange={(e) => {
                        const next = [...weekdays];
                        next[idx] = e.target.checked;
                        setWeekdays(next);
                      }}
                    />
                  ))}
                </Stack>
              </FormControl>
              <FormControl>
                <FormLabel>Time (24h)</FormLabel>
                <Input
                  type="time"
                  value={hhmm}
                  onChange={(e) => setHhmm(e.target.value)}
                  slotProps={{ input: { step: 300 } }}
                />
              </FormControl>
            </>
          )}

          {repeatMode === "monthly" && (
            <Stack direction="row" spacing={1}>
              <FormControl sx={{ width: 120 }}>
                <FormLabel>Day of month</FormLabel>
                <Input
                  type="number"
                  value={monthDay}
                  onChange={(e) =>
                    setMonthDay(
                      Math.min(
                        28,
                        Math.max(1, parseInt(e.target.value, 10) || 1),
                      ),
                    )
                  }
                  slotProps={{ input: { min: 1, max: 28 } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Time (24h)</FormLabel>
                <Input
                  type="time"
                  value={hhmm}
                  onChange={(e) => setHhmm(e.target.value)}
                  slotProps={{ input: { step: 300 } }}
                />
              </FormControl>
            </Stack>
          )}

          {!debugEnabled && repeatMode === "custom" && (
            <Alert color="warning" variant="soft">
              This heartbeat uses a custom cron schedule. Add{" "}
              <code>?debug=1</code> to the URL to edit the raw expression, or
              pick a preset above to replace it.
            </Alert>
          )}

          {debugEnabled && repeatMode === "custom" && (
            <FormControl>
              <FormLabel>Cron expression</FormLabel>
              <Input
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                placeholder="m h dom mon dow"
                data-testid="heartbeat-editor-custom-cron"
              />
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Standard 5-field cron. Stored as-is; hub validates.
              </Typography>
            </FormControl>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2, justifyContent: "flex-end" }}
        >
          <Button
            variant="plain"
            color="neutral"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            onClick={handleSave}
            disabled={!canSave || submitting}
            loading={submitting}
            data-testid="heartbeat-editor-save"
          >
            Save
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
